/**
 * Left Sheet Component
 * Sheet that slides from the left (for mobile sidebar)
 * Based on the existing Sheet component but slides from left instead of right
 * 
 * @author Varun Pandey
 */

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface LeftSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: React.ReactNode;
    title?: string;
    description?: string;
}

// iOS-optimized easing curves
const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1.0];
const iosExitEasing: [number, number, number, number] = [0.4, 0.0, 1, 1];

// Left drawer variants (slides from left)
const leftDrawerVariants = {
    closed: {
        x: '-100%',
        transition: {
            duration: 0.3,
            ease: iosExitEasing,
        },
    },
    open: {
        x: 0,
        transition: {
            duration: 0.35,
            ease: iosEasing,
        },
    },
};

// Overlay variants with blur effect
const overlayVariants = {
    closed: {
        opacity: 0,
        backdropFilter: 'blur(0px)',
        transition: {
            duration: 0.2,
        },
    },
    open: {
        opacity: 1,
        backdropFilter: 'blur(8px)',
        transition: {
            duration: 0.3,
        },
    },
};

export const LeftSheet: React.FC<LeftSheetProps> = ({
    open,
    onOpenChange,
    children,
    title,
    description,
}) => {
    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <AnimatePresence>
                {open && (
                    <Dialog.Portal forceMount>
                        {/* Overlay with blur */}
                        <Dialog.Overlay asChild>
                            <motion.div
                                variants={overlayVariants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                                className="fixed inset-0 z-50 bg-black/50"
                                style={{
                                    willChange: 'opacity, backdrop-filter',
                                    WebkitBackfaceVisibility: 'hidden',
                                    backfaceVisibility: 'hidden',
                                }}
                            />
                        </Dialog.Overlay>

                        {/* Left Drawer */}
                        <Dialog.Content asChild forceMount>
                            <motion.div
                                variants={leftDrawerVariants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                                className={cn(
                                    'fixed z-50 flex flex-col gap-4 bg-[#1C1C1E]/60 backdrop-blur-xl shadow-lg',
                                    'inset-y-0 left-0 right-auto',
                                    'w-72 rounded-r-[1.5rem] border-r border-[#3A3A3C]',
                                    'overflow-y-auto'
                                )}
                                data-ptr-ignore
                                style={{
                                    willChange: 'transform',
                                    WebkitBackfaceVisibility: 'hidden',
                                    backfaceVisibility: 'hidden',
                                    WebkitTransform: 'translateZ(0)',
                                    transform: 'translateZ(0)',
                                }}
                            >
                                {(title || description) && (
                                    <div className="p-6 pb-0">
                                        {title && (
                                            <Dialog.Title className="text-2xl font-bold text-white mb-2">
                                                {title}
                                            </Dialog.Title>
                                        )}
                                        {description && (
                                            <Dialog.Description className="text-sm text-[#8E8E93]">
                                                {description}
                                            </Dialog.Description>
                                        )}
                                    </div>
                                )}
                                <Dialog.Close asChild>
                                    <button
                                        type="button"
                                        onClick={() => onOpenChange(false)}
                                        className={cn(
                                            'absolute right-4 top-4 z-20 rounded-sm opacity-70 ring-offset-background transition-opacity',
                                            'hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                                            'disabled:pointer-events-none'
                                        )}
                                    >
                                        <X className="h-5 w-5 text-white" />
                                        <span className="sr-only">Close</span>
                                    </button>
                                </Dialog.Close>
                                <div className="flex flex-col w-full">
                                    {children}
                                </div>
                            </motion.div>
                        </Dialog.Content>
                    </Dialog.Portal>
                )}
            </AnimatePresence>
        </Dialog.Root>
    );
};
